// src/mocks/handlers.js
import { HttpResponse, http } from 'msw'
import { apiBase } from '../utils/base-url'
import {
  cargoParserMock,
  fixedCargoMock,
  openCargoMock,
  subsCargoMock,
} from './mock-data/cargo'
import { mockComments } from './mock-data/comments'
import { dashboardSummaryMockData } from './mock-data/dashboard/summary'
import { mockForecast } from './mock-data/forecast'
import { forecastComments } from './mock-data/forecast-comments'
import { poolPartnerList } from './mock-data/list/pool-partner-list'
import { vesselsList } from './mock-data/list/vessels-list'
import { dummyUserVt } from './mock-data/me/me-vt'
import { notificationBannerMock } from './mock-data/notification-banner'
import { mockPoolPoints } from './mock-data/pool-points'
import { poolsMock } from './mock-data/pools'
import {
  positionListMock,
  positionListStatusMock,
} from './mock-data/position-list'
import { mockPreFixture } from './mock-data/pre-fixture/mock-pre-fixture'
import { reportTypeMock, reportWithIdMock } from './mock-data/reports'
import { mockSanctionClauses } from './mock-data/sanction-clauses'
import { mockUsers } from './mock-data/users'
import { dummyVessel } from './mock-data/vessels'
import { mockBasicVesselData } from './mock-data/vessels/basic-vessel-data'
import { mockGeneralInfo } from './mock-data/vessels/general-info'
import { patchVesselsStatusResMock } from './mock-data/vessels/patch-vessels-res'
import { postVesselsResMock } from './mock-data/vessels/post-vessels-res'
import { mockVesselComments } from './mock-data/vessels/vessel-comments'

// Mock vessels with fixture-appropriate voyages for Your Fixtures modal
const fixturesMockVessels = [
  {
    vesselId: 'mock-vessel-1',
    vessel: 'MT Atlantic Star',
    pool: 'MR',
    imoNumber: '1234567',
    voyages: [
      {
        vesselCode: 'MOCK1',
        vesselName: 'MT Atlantic Star',
        voyageNumber: 'V001',
        status: 'Commenced',
        commenceDate: '2026-05-01T10:00:00.000Z',
        CompleteDate: null,
        cargoGroup: 'CPP',
        fixedBy: 'johndoe@example.com',
        voyageNotes: [],
        dailyTceImos: {
          tceDeltaYesterday: 1250,
          deltaActualEstimatedDays: 2,
          tceDeltaMondayToFriday: 800,
        },
      },
    ],
  },
  {
    vesselId: 'mock-vessel-2',
    vessel: 'MT Pacific Voyager',
    pool: 'MR',
    imoNumber: '2345678',
    voyages: [
      {
        vesselCode: 'MOCK2',
        vesselName: 'MT Pacific Voyager',
        voyageNumber: 'V042',
        status: 'Commenced',
        commenceDate: '2026-05-03T10:00:00.000Z',
        CompleteDate: null,
        cargoGroup: 'DPP',
        fixedBy: 'johndoe@example.com',
        voyageNotes: [],
        dailyTceImos: {
          tceDeltaYesterday: -750,
          deltaActualEstimatedDays: -1,
          tceDeltaMondayToFriday: -600,
        },
      },
    ],
  },
  {
    vesselId: 'mock-vessel-3',
    vessel: 'MT Northern Light',
    pool: 'Handy',
    imoNumber: '3456789',
    voyages: [
      {
        vesselCode: 'MOCK3',
        vesselName: 'MT Northern Light',
        voyageNumber: 'V023',
        status: 'Completed',
        commenceDate: '2026-04-25T10:00:00.000Z',
        CompleteDate: '2026-05-02T10:00:00.000Z',
        cargoGroup: 'CPP',
        fixedBy: 'johndoe@example.com',
        voyageNotes: [],
        dailyTceImos: {
          tceDeltaYesterday: 520,
          deltaActualEstimatedDays: 0,
          tceDeltaMondayToFriday: 520,
        },
      },
    ],
  },
]

export const handlers = [
  http.get(`${apiBase()}/vessels/:vesselId/poolpoints`, () => {
    return HttpResponse.json(mockPoolPoints, { status: 200 })
  }),
  http.get(`${apiBase()}/prefixture/:preFixtureId`, () => {
    return HttpResponse.json(mockPreFixture, { status: 200 })
  }),
  http.get(`${apiBase()}/prefixture`, () => {
    return HttpResponse.json([], { status: 200 })
  }),
  http.get(`${apiBase()}/sanction-clauses`, ({ request }) => {
    const url = new URL(request.url)
    const search = url.searchParams.get('search')
    if (search) {
      const searchLower = search.toLowerCase()
      const filtered = mockSanctionClauses.filter(
        (clause) =>
          clause.title.toLowerCase().includes(searchLower) ||
          clause.answer.toLowerCase().includes(searchLower),
      )
      return HttpResponse.json(filtered, { status: 200 })
    }
    return HttpResponse.json(mockSanctionClauses, { status: 200 })
  }),
  http.patch(
    `${apiBase()}/sanction-clauses/:id`,
    async ({ request, params }) => {
      let body
      try {
        body = await request.json()
      } catch {
        body = {}
      }
      const currentIndex = mockSanctionClauses.findIndex(
        (c) => c._id === params.id,
      )
      if (currentIndex === -1) {
        return HttpResponse.json({ error: 'Not found' }, { status: 404 })
      }

      // Handle rank-based reordering
      if (body.order !== undefined) {
        const newPosition = body.order
        const totalCount = mockSanctionClauses.length

        // Validate integer
        if (!Number.isInteger(newPosition)) {
          return HttpResponse.json(
            { error: 'Position must be an integer' },
            { status: 400 },
          )
        }

        // Validate bounds
        if (newPosition < 1 || newPosition > totalCount) {
          return HttpResponse.json(
            { error: `Position must be between 1 and ${totalCount}` },
            { status: 400 },
          )
        }

        const newRank = newPosition - 1
        if (currentIndex !== newRank) {
          // Remove from current position
          const [movedItem] = mockSanctionClauses.splice(currentIndex, 1)
          // Insert at new position
          mockSanctionClauses.splice(newRank, 0, movedItem)
          // Reassign all orders sequentially
          mockSanctionClauses.forEach((c, i) => {
            c.order = i + 1
          })
        }
        delete body.order
      }

      // Update other fields
      const updatedIndex = mockSanctionClauses.findIndex(
        (c) => c._id === params.id,
      )
      mockSanctionClauses[updatedIndex] = {
        ...mockSanctionClauses[updatedIndex],
        ...body,
      }
      return HttpResponse.json(mockSanctionClauses[updatedIndex])
    },
  ),
  http.delete(`${apiBase()}/sanction-clauses/:id`, ({ params }) => {
    const index = mockSanctionClauses.findIndex((c) => c._id === params.id)
    if (index === -1) {
      return HttpResponse.json({ error: 'Not found' }, { status: 404 })
    }
    mockSanctionClauses.splice(index, 1)
    // Reassign all orders sequentially
    mockSanctionClauses.forEach((c, i) => {
      c.order = i + 1
    })
    return HttpResponse.json({ ok: 'OK', id: params.id })
  }),
  http.post(`${apiBase()}/sanction-clauses`, async ({ request }) => {
    const body = await request.json()
    const newId = `mock-${Date.now()}`
    const maxOrder = Math.max(
      0,
      ...mockSanctionClauses.map((c) => c.order || 0),
    )
    const targetOrder = body.order || maxOrder + 1

    // Shift existing items at or after the target position
    mockSanctionClauses.forEach((c) => {
      if (c.order >= targetOrder) {
        c.order += 1
      }
    })

    const newClause = {
      _id: newId,
      title: body.title,
      answer: body.answer,
      order: targetOrder,
    }
    mockSanctionClauses.push(newClause)
    // Sort by order for consistency
    mockSanctionClauses.sort((a, b) => a.order - b.order)
    return HttpResponse.json({ ok: 'OK', id: newId })
  }),
  http.get(`${apiBase()}/users`, () => {
    return HttpResponse.json(mockUsers, { status: 200 })
  }),
  http.get(`${apiBase()}/positionlist/status`, () => {
    return HttpResponse.json(positionListStatusMock, { status: 200 })
  }),
  http.get(`${apiBase()}/positionlist/commercialmanagementvessels`, () => {
    return HttpResponse.json([], { status: 200 })
  }),
  http.get(`${apiBase()}/positionlist/vessels`, () => {
    // Include fixtures mock vessels along with regular position list
    return HttpResponse.json([...positionListMock, ...fixturesMockVessels], {
      status: 200,
    })
  }),
  http.get(`${apiBase()}/vessels/:vesselId/general-info`, () => {
    return HttpResponse.json(mockGeneralInfo, { status: 200 })
  }),
  http.get(`${apiBase()}/vessels/:vesselId/comments`, () => {
    return HttpResponse.json(mockVesselComments, { status: 200 })
  }),
  http.get(`${apiBase()}/vessels/:vesselId/data`, ({ request }) => {
    const url = new URL(request.url)
    const tab = url.searchParams.get('tab')

    if (tab === 'basicvesseldata') {
      return HttpResponse.json(mockBasicVesselData, { status: 200 })
    }

    return HttpResponse.json({}, { status: 200 })
  }),
  http.get(`${apiBase()}/reports`, ({ request }) => {
    const url = new URL(request.url)
    const type = url.searchParams.get('type')
    const reportId = url.searchParams.get('reportId')
    const includeEmbedToken = url.searchParams.get('includeEmbedToken')

    if (type === 'euets-report') {
      return HttpResponse.json(reportTypeMock, { status: 200 })
    }

    if (reportId && includeEmbedToken === 'true') {
      return HttpResponse.json(reportWithIdMock, { status: 200 })
    }
  }),
  http.get(`${apiBase()}/me`, () => {
    return HttpResponse.json(dummyUserVt, { status: 200 })
  }),
  http.patch(`${apiBase()}/me`, () => {
    return HttpResponse.json({ status: 200 })
  }),
  http.get(`${apiBase()}/vessels`, () => {
    return HttpResponse.json([dummyVessel], { status: 200 })
  }),
  http.get(`${apiBase()}/posts`, () => {
    return HttpResponse.json(forecastComments, { status: 200 })
  }),
  http.get(`${apiBase()}/pools`, () => {
    return HttpResponse.json(poolsMock, { status: 200 })
  }),
  // http.get('https://maersktankers-stage.digital/api/marketing/signups', () => {
  //   return HttpResponse.json(testResponse, { status: 200 })
  // }),
  http.get(`${apiBase()}/dashboard/summary`, () => {
    return HttpResponse.json(dashboardSummaryMockData, { status: 200 })
  }),
  http.get(`${apiBase()}/pools/:poolName/comments`, () => {
    return HttpResponse.json(mockComments, { status: 200 })
  }),
  http.get(`${apiBase()}/forecast/calculated`, () => {
    return HttpResponse.json(mockForecast, { status: 200 })
  }),
  http.post(`${apiBase()}/vessels`, () => {
    return HttpResponse.json(postVesselsResMock, { status: 200 })
  }),
  http.patch(`${apiBase()}/vessels/:vesselId/status`, () => {
    return HttpResponse.json(patchVesselsStatusResMock, { status: 200 })
  }),
  http.get(`${apiBase()}/q88/list`, () => {
    return HttpResponse.json(vesselsList, { status: 200 })
  }),
  http.get(`${apiBase()}/poolpartners/list`, () => {
    return HttpResponse.json(poolPartnerList, { status: 200 })
  }),
  http.get(`${apiBase()}/notification-banners`, () => {
    return HttpResponse.json(notificationBannerMock, { status: 200 })
  }),
  http.get(`${apiBase()}/cargo/cargo-grades`, () => {
    return HttpResponse.json(['CPP', 'DPP'], { status: 200 })
  }),
  http.get(`${apiBase()}/cargo`, ({ request }) => {
    const url = new URL(request.url)
    const status = url.searchParams.get('status')

    if (status === 'Open') {
      return HttpResponse.json([openCargoMock], { status: 200 })
    }

    if (status === 'On Subs') {
      return HttpResponse.json([subsCargoMock], { status: 200 })
    }
    if (status === 'Fixed') {
      return HttpResponse.json([fixedCargoMock], { status: 200 })
    }
  }),

  http.get(`${apiBase()}/cargo/:cargoId`, () => {
    return HttpResponse.json(openCargoMock, { status: 200 })
  }),
  http.get(`${apiBase()}/tabs/:tabId`, () => {
    return HttpResponse.json([], { status: 200 })
  }),
  http.get(`${apiBase()}/settings/:settingName`, () => {
    return HttpResponse.json(null, { status: 200 })
  }),
  // Generic vessel endpoint - must come after specific vessel sub-routes
  http.get(`${apiBase()}/vessels/:vesselId`, () => {
    return HttpResponse.json(dummyVessel, { status: 200 })
  }),
  http.post('https://maersktankers-test.digital/ai/cargo/parse-cargoes', () => {
    return HttpResponse.json(cargoParserMock, { status: 200 })
  }),
]
